import { useContext, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { AppContext } from "../App";
import { Ecrf, EcrfSectionExtraProperties, Patient, FormData, EcrfSection, Inquiry, FieldTypeEnum, isPatientEligible, parseScreeningFailures } from "../models/models";
import { Row, Col, Anchor, List, Menu, Tabs, Button, Card, Typography, Descriptions, Space, Modal, Avatar, ConfigProvider, Tooltip, Result, type MenuProps } from "antd";
import { red, grey, blue, yellow, green } from '@ant-design/colors';
import { FormView } from "../components/FormView";
import { Content } from "antd/es/layout/layout";
import moment from "moment";
import { SelectPatient } from "../components/SelectPatientModal";
import { getFormValues, getInquiriesForSection, getPatientAdmission, getPatients } from "../models/serverApi";
import { SelectAdmission } from "../components/SelectAdmissionModal";
import { CheckCircleFilled, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, ArrowLeftOutlined, SwapOutlined } from "@ant-design/icons";
import { Prompt } from "../helpers";
import { getViewTheme, useEcrfView } from "../theme/EcrfViewTheme";
import { EcrfViewSwitcher } from "../components/EcrfViewSwitcher";

// Query-string ids are user-editable text: anything non-numeric yields null so a
// malformed URL falls back to the default selection instead of NaN.
const parseIntOrNull = (raw: string | null): number | null => {
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
};

export const EcrfPage: React.FC = () => {
    const [context, setContext] = useContext(AppContext);
    const { view, align } = useEcrfView();
    const [ecrf, setEcrf] = useState<Ecrf>();
    // Set by FormView whenever the open section holds unsaved edits. Guards both
    // section switching and leaving the page - with 51 fields in Section I, a
    // silent discard is an expensive mistake.
    const [formDirty, setFormDirty] = useState(false);
    const [selectedSection, setSelectedSection] = useState(-1);
    const [selectPatientModalOpen, setSelectPatientModalOpen] = useState(false);
    const [selectPatientAdmissionModalOpen, setSelectPatientAdmissionModalOpen] = useState(false);
    const [patients, setPatients] = useState<Array<Patient>>([]);
    const [formValues, setFormValues] = useState<FormData | null>();
    const [allFormValues, setAllFormValues] = useState<Array<FormData> | null>(null);
    const [pendingInquiries, setPendingInquiries] = useState<Inquiry[]>([]);
    const [highlightedFieldId, setHighlightedFieldId] = useState<number | undefined>(undefined);
    let { id } = useParams<{ id: string | undefined }>();
    const [refresh, setRefresh] = useState<boolean>(false);
    const { confirm } = Modal;
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {

        if (id == null || id == undefined) {
            return;
        }

        var nid = parseInt(id);

        var e = context.ecrfs.filter(e => e.id == nid)?.[0];
        if (!e) {
            return;
        }

        // Order the sections by their protocol letter, not by the order the rows
        // happen to come back in.
        //
        // Nothing sorts them server-side, so they arrived in EcrfSections.Id order -
        // fine while every section was created together, but H4 was added later and
        // so took the highest id and rendered after I, at the very end. Sorting on
        // the name puts it where the protocol says: D, E, F, G, H1..H4, I.
        //
        // localeCompare with numeric:true so H10 would follow H9 rather than H1,
        // if the CRF ever grows that far.
        const sorted = {
            ...e,
            sections: [...(e.sections ?? [])].sort((a, b) =>
                `${a.name ?? ""}`.localeCompare(`${b.name ?? ""}`, "el", { numeric: true })),
        };

        setEcrf(sorted);

        // Read deep-link query params
        const qSectionId = searchParams.get("sectionId");
        const qFieldId = searchParams.get("fieldId");

        if (qSectionId) {
            setSelectedSection(parseInt(qSectionId));
        } else {
            setSelectedSection(e?.sections?.[0].id ?? -1);
        }

        if (qFieldId) {
            setHighlightedFieldId(parseInt(qFieldId));
        }

        setContext({ ...context, selection: { patient: context.selection?.patient, admission: context.selection?.admission, clinicalTrial: e.trial } });

    }, [id, context.ecrfs]);

    // Restore patient/admission either from location.state (Dashboard inquiry click)
    // or from the URL (?patientId=&admissionId=). The URL copy is what survives a
    // browser reload: selection lives in React context, which is lost on refresh, so
    // without it the page reopens with no patient and no data.
    //
    // Both sources are one-shot restore *hints*, not standing truth. Once a
    // selection exists in context, the user owns it - picking someone else in the
    // Επιλογή modal must stick.
    //
    // location.state in particular is sticky: setSearchParams(replace) rewrites the
    // query string but leaves history state untouched, so state.patientId kept
    // naming the patient the user arrived with. Because it was read first
    // (`state ?? url`), every later selection was overwritten by it a moment after
    // being made - the page snapped back to the old patient, which re-armed the
    // URL-mirror effect, which re-armed this one. That was the reload loop.
    const restoredRef = useRef(false);

    useEffect(() => {
        if (restoredRef.current) return;

        // A selection already in context (normal in-app navigation) needs no
        // restoring, and must not be second-guessed.
        if (context.selection?.patient?.id && context.selection?.admission?.id) {
            restoredRef.current = true;
            return;
        }

        const state = location.state as { patientId?: number; admissionId?: number } | null;

        const patientId = state?.patientId ?? parseIntOrNull(searchParams.get("patientId"));
        const admissionId = state?.admissionId ?? parseIntOrNull(searchParams.get("admissionId"));

        if (!patientId || !admissionId) return;

        // Claim the restore before the await: without this, a second render during
        // the fetch would start an identical one.
        restoredRef.current = true;

        Promise.all([getPatients(), getPatientAdmission()]).then(([pts, admissions]) => {
            const patient = pts.find(p => p.id === patientId);
            const admission = admissions.find(a => a.id === admissionId);
            if (patient && admission) {
                setContext(prev => ({
                    ...prev,
                    selection: { ...prev.selection, patient, admission } as any
                }));
            }
        });
    }, [location.state, searchParams, context.selection?.patient?.id, context.selection?.admission?.id]);

    // Mirror the current selection into the URL so a reload (or a copied link)
    // reopens exactly where the user was. Uses replace so it does not add a history
    // entry per section click - the Back button should leave the eCRF, not step back
    // through every section visited.
    useEffect(() => {
        if (!ecrf?.id) return;

        const next = new URLSearchParams(searchParams);

        const setOrDelete = (key: string, value: number | null | undefined) => {
            if (value == null || Number.isNaN(value) || value < 0) next.delete(key);
            else next.set(key, `${value}`);
        };

        setOrDelete("sectionId", selectedSection);
        setOrDelete("patientId", context.selection?.patient?.id);
        setOrDelete("admissionId", context.selection?.admission?.id);

        if (next.toString() !== searchParams.toString()) {
            setSearchParams(next, { replace: true });

            // Drop the arrival hint once the URL carries the selection itself.
            // setSearchParams does not touch history state, so without this the
            // Dashboard's { patientId, admissionId } would outlive the selection it
            // described and reappear on the next reload.
            if (location.state) {
                window.history.replaceState(null, "");
            }
        }
    }, [ecrf?.id, selectedSection, context.selection?.patient?.id, context.selection?.admission?.id]);

    useEffect(() => {
        if (!ecrf?.id || !context.selection?.patient || !context.selection?.admission) {
            return;
        }

        fetchAllData();
    }, [ecrf?.id, selectedSection, context.selection?.patient, context.selection?.admission]);

    useEffect(() => {
        if (!refresh) {
            return;
        }

        setAllFormValues(null);
        fetchAllData();
        setRefresh(false);
    }, [refresh]);

    // Fetch pending inquiries for the current section
    useEffect(() => {
        if (!context.selection?.admission?.id) {
            setPendingInquiries([]);
            return;
        }
        const ss = getSelectedSection();
        if (!ss?.formId) {
            setPendingInquiries([]);
            return;
        }
        getInquiriesForSection(ss.formId, ss.id, context.selection.admission.id as number)
            .then(setPendingInquiries);
    }, [selectedSection, context.selection?.admission?.id]);

    const fetchAllData = () => {
        if (!ecrf?.id || !context.selection?.patient || !context.selection?.admission) {
            return;
        }

        let ss = getSelectedSection();
        if (!ss || !ss.formId) {
            return;
        }
        getFormValues(ss?.formId, ss?.id, context?.selection?.admission?.id ?? 0).then((fv) => {
            setFormValues(fv);
        });

        if (allFormValues != null && refresh != true) {
            return;
        }

        let allSections = [...ecrf.sections];

        for (let i = 0; i < allSections.length; i++) {
            let s = allSections[i];
            if (!s || s.formId == null) {
                if ((s?.children?.length ?? 0) > 0) {
                    allSections.push(...s.children);
                }
                continue;
            }

            getFormValues(s.formId, s?.id, context?.selection?.admission?.id ?? 0).then((fv) => {
                if (!fv) {
                    return;
                }

                setAllFormValues(p => {
                    if (!p) {
                        return [fv];
                    }

                    return [...p, fv];
                });
            });
        }
    }
    const calculateSectionState = (section: EcrfSection) => {
        if (!section || section.formId == null || section?.children?.length > 0) {
            return;
        }

        let formV = formValues?.sectionId == section.id ?
            formValues :
            allFormValues?.find(afv => afv.formId == section.formId && afv.sectionId == section.id);

        if (!formV || !section || !section?.form) {
            return <CloseCircleOutlined style={{ color: red[6] }} />;
        }

        let hasAny = false;
        let hasAll = true;
        for (let i = 0; i < section.form.formFields.length; i++) {
            let fv = section.form.formFields[i];

            let value = formV.formFieldValues.find(ffv => ffv.fieldId == fv.id);
            if (value == null) {
                hasAll = false;
                break;
            }

            hasAny = true;
        }



        if (hasAll) {
            return <CheckCircleOutlined style={{ color: green[6] }} />;
        }

        if (hasAny) {
            return <CheckCircleOutlined style={{ color: grey[6] }} />;
        }

        return <CloseCircleOutlined style={{ color: red[6] }} />;
    }

    // Every navigable section in menu order, parents and children flattened.
    // Sections with children are containers only - they hold no form of their
    // own - so "next" must step over them rather than land on an empty page.
    const getNavigableSections = (): EcrfSection[] => {
        const out: EcrfSection[] = [];
        (ecrf?.sections ?? []).forEach(s => {
            if ((s.children?.length ?? 0) > 0) {
                s.children.forEach(c => out.push(c));
            }
            else if (s.formId != null) {
                out.push(s);
            }
        });
        return out;
    };

    // How many of a section's answerable fields have a value, for the "12/22"
    // badge in the menu. Derived, mirror and layout fields are excluded: the
    // investigator cannot fill them, so counting them would make a finished
    // section look incomplete.
    const getSectionProgress = (section: EcrfSection): { done: number, total: number } | null => {
        const fields = section?.form?.formFields;
        if (!fields || section.formId == null) return null;

        const formV = formValues?.sectionId === section.id
            ? formValues
            : allFormValues?.find(afv => afv.formId === section.formId && afv.sectionId === section.id);

        // The stored value of another field on this section, for evaluating a
        // Conditional. Uses the same saved data the badge is counting, so the
        // denominator matches what the investigator was actually asked.
        const storedValue = (fieldId: number): string => {
            const v = formV?.formFieldValues?.find(ffv => ffv.fieldId === fieldId);
            if (!v || v.formValue == null) return "";
            return `${v.formValue}`.trim().replace(/^"|"$/g, "");
        };

        // A conditional field only counts when its condition is met. Section G is
        // 12-of-22 conditional: counting fields the form never showed made a
        // finished section read 14/22 and the badge stopped meaning anything.
        const isApplicable = (ff: any): boolean => {
            if (!ff.extraStyle) return true;
            try {
                const c = JSON.parse(ff.extraStyle)?.Conditional;
                if (!c?.fieldId) return true;
                return storedValue(c.fieldId) === `${c.value ?? ""}`.trim();
            }
            catch {
                return true;
            }
        };

        const answerable = fields.filter(ff =>
            ff.fieldType !== FieldTypeEnum.Label
            && ff.fieldType !== FieldTypeEnum.Seperator
            && !(ff.extraStyle?.includes("Formula"))
            && !(ff.extraStyle?.includes("DerivedCategories"))
            && !(ff.extraStyle?.includes("MirrorOf"))
            && isApplicable(ff));

        if (answerable.length === 0) return null;

        const done = answerable.filter(ff => {
            const v = formV?.formFieldValues?.find(ffv => ffv.fieldId === ff.id);
            if (!v) return false;
            // null is a deliberate "not set" / not applicable - that counts as
            // answered, otherwise a correctly completed section never reaches
            // 100% and the badge stops meaning anything.
            if (v.formValue === null) return true;
            const raw = `${v.formValue}`.trim();
            return raw !== "" && raw !== '""' && raw !== "[]";
        }).length;

        return { done, total: answerable.length };
    };

    const getSectionItems = (): MenuProps['items'] => {
        let isAdmin = context?.user?.admin === true;
        return ecrf?.sections.map(s => {
            let extraProperties: EcrfSectionExtraProperties | null = null;
            try {
                extraProperties = s.extraJson ? JSON.parse(s.extraJson) : null;
            }
            catch {
            }

            const isDisabled = () => {
                return (extraProperties?.isAdminOnly === true && !isAdmin);
            }

            // "12/22" beside the name: the state icon says complete/partial/empty,
            // but not how much is left. With 51 fields in one section that is the
            // thing an investigator most needs in order to know where to resume.
            // Section names are long - six of the nine clip even in the widened
            // rail - so the label ellipsizes and the full name is available on
            // hover. The tooltip wraps the whole item, so it also covers the case
            // where the progress badge is what pushed the text out.
            const withProgress = (sec: EcrfSection, text: React.ReactNode) => {
                const p = getSectionProgress(sec);
                const body = p
                    ? (
                        <span className="ecrf-menu-item">
                            <span className="ecrf-menu-label">{text}</span>
                            <span className={`ecrf-menu-count${p.done >= p.total ? " is-complete" : ""}`}>
                                {p.done}/{p.total}
                            </span>
                        </span>
                    )
                    : <span className="ecrf-menu-label">{text}</span>;

                return (
                    <Tooltip title={sec.name} placement="right" mouseEnterDelay={0.4}>
                        {body}
                    </Tooltip>
                );
            };

            const hasChildren = s.children && s.children.length > 0;
            return {
                key: s.id,
                label: hasChildren
                    ? <span style={{ fontStyle: "italic" }}>{s.name}</span>
                    : withProgress(s, s.name),
                type: 'item',
                icon: calculateSectionState(s),
                disabled: isDisabled() ? true : false,
                ...(hasChildren ? {
                    children: s.children.map(ss => ({
                        key: ss.id,
                        label: withProgress(ss, ss.name),
                        type: 'item',
                        icon: calculateSectionState(ss),
                    }))
                } : {})
            };
        });
    }

    const linkClick = (e: any, link: { title: React.ReactNode, href: string }) => {
        setSelectedSection(parseInt(link.href));
    }

    const getSelectedSection = () => {
        for (let i = 0; i < (ecrf?.sections?.length ?? 0); i++) {
            if (ecrf?.sections[i].id == selectedSection) {
                return ecrf?.sections[i];
            }

            for (let j = 0; j < (ecrf?.sections[i]?.children?.length ?? 0); j++) {
                if (ecrf?.sections[i].children[j].id == selectedSection) {
                    return ecrf?.sections[i].children[j];
                }
            }
        }
    }

    const selectPatient = () => {
        setSelectPatientModalOpen(true);
    }

    const selectPatientAdmission = () => {
        setSelectPatientAdmissionModalOpen(true);
    }

    const cancelselectPatient = () => {
        setSelectPatientModalOpen(false);
    }
    const okSelectPatient = () => {
        setSelectPatientModalOpen(false);
        setSelectedSection(ecrf?.sections?.[0].id ?? -1);
    }

    const cancelSelectPatientAdmission = () => {
        setSelectPatientAdmissionModalOpen(false);
    }
    const okSelectPatientAdmission = () => {
        setSelectPatientAdmissionModalOpen(false);
    }

    const handleInquiryResolved = (inquiryId: number) => {
        setPendingInquiries(prev => prev.filter(i => i.id !== inquiryId));
    };

    // The app header lives in DashboardLayout, a sibling of this page, so it cannot
    // read the view from context. Stamp it on <body> while this page is mounted:
    // the rail view wants a dark top bar continuous with its rail, as in the Split
    // Rail design. Removed on unmount, so other pages keep the normal header.
    useEffect(() => {
        document.body.setAttribute("data-ecrf-view", view);
        return () => document.body.removeAttribute("data-ecrf-view");
    }, [view]);

    // Browser-level guard: covers reload, tab close and Back, which React cannot
    // intercept. The confirm() in requestSection covers in-app navigation.
    useEffect(() => {
        if (!formDirty) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            // Browsers show their own wording; a non-empty returnValue is what
            // actually triggers the prompt.
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [formDirty]);

    // Switching section unmounts the form and discards whatever is in it, so ask
    // first. Routed through here by every view's menu.
    const requestSection = (sectionId: number) => {
        if (`${sectionId}` === `${selectedSection}`) return;

        const go = () => {
            setFormDirty(false);
            // antd Menu hands its keys back as strings; store a number so every
            // later `===` against a section id holds. Comparing "3007" with 3007
            // silently failed and left "save and next" permanently disabled.
            setSelectedSection(Number(sectionId));
            setHighlightedFieldId(undefined);
        };

        if (!formDirty) {
            go();
            return;
        }

        confirm({
            icon: <ExclamationCircleOutlined />,
            title: "Μη αποθηκευμένες αλλαγές",
            content: "Υπάρχουν αλλαγές που δεν έχουν αποθηκευτεί. Αν συνεχίσετε θα χαθούν.",
            okText: "Συνέχεια χωρίς αποθήκευση",
            okButtonProps: { danger: true },
            cancelText: "Ακύρωση",
            onOk: go,
        });
    };

    const goBack = () => {
        confirm({
            icon: <ExclamationCircleOutlined />,
            okText: "Ναι",
            cancelText: "Όχι",
            content: "Σίγουρα θέλετε να φύγετε απο την σελίδα;",
            onOk() { window.history.back(); },
            onCancel() { },
        });
    };

    const trialTitle = context.selection?.patient?.clinicalTrial?.title ?? "Κλινική δοκιμή";
    const patientName = context.selection?.patient?.patientName ?? "Επιλέξτε ασθενή";
    const patientDescription = context.selection?.patient?.patientDescription ?? "Επιλέξτε ασθενή";

    // The section menu, shared by all three views. Only its `theme` differs -
    // the rail paints a dark surface, so the menu must render dark inside it.
    const sectionMenu = (dark: boolean) => (
        <Menu mode="inline" forceSubMenuRender={true} expandIcon={<></>}
            theme={dark ? "dark" : "light"}
            style={dark ? { background: "transparent", borderInlineEnd: "none" } : undefined}
            openKeys={ecrf?.sections.map(s => s.id.toString())} items={getSectionItems()}
            selectedKeys={[selectedSection.toString()]}
            onSelect={(info: any) => { requestSection(info.key); }}>
        </Menu>
    );

    // The form itself is identical in every view. Keyed by section so each one
    // gets its own useForm() store: field names are `field-<id>` and ids repeat
    // across forms (FormFields is keyed on (FormId, Id)), so a shared store would
    // hand the previous section's value to a control of a different type - e.g.
    // Section D's sex code "1" reaching Section E's DatePicker, which throws
    // inside antd. A fresh instance also avoids clearing the store mid-render,
    // which would update already-mounted Form.Items.
    const navigable = getNavigableSections();
    const currentIndex = navigable.findIndex(s => s.id === selectedSection);
    const nextSection = currentIndex >= 0 ? navigable[currentIndex + 1] : undefined;

    // Screened-out patients are recorded for the exclusion statistics but collect
    // no CRF data. The dashboard disables «Συμπλήρωση» for them; this covers the
    // other ways in - a bookmarked URL, the browser Back button, a link shared
    // between colleagues - so the rule holds wherever the page is reached from.
    //
    // Replaces the form for every view, rather than being added to each of the
    // three shells separately.
    const selectedPatient = context.selection?.patient;
    const patientIneligible = selectedPatient != null && !isPatientEligible(selectedPatient);

    const ineligibleNotice = (() => {
        const failures = parseScreeningFailures(selectedPatient?.screeningFailuresJson);
        const reasons = [
            ...failures.failedInclusion.map(t => ({ kind: "Δεν πληροί", text: t })),
            ...failures.metExclusion.map(t => ({ kind: "Αποκλεισμός", text: t })),
        ];

        return (
            <Result
                status="warning"
                title="Ο ασθενής δεν πληροί τα κριτήρια της μελέτης"
                subTitle={
                    <>
                        Ο ασθενής <strong>{selectedPatient?.patientName}</strong> έχει καταγραφεί για
                        στατιστικούς λόγους, αλλά δεν συμμετέχει στη μελέτη, επομένως δεν είναι
                        δυνατή η συμπλήρωση του CRF.
                    </>
                }
                extra={[
                    <Button key="back" type="primary" onClick={() => navigate("/dashboard")}>
                        Επιστροφή στη λίστα ασθενών
                    </Button>,
                    <Button key="other" onClick={selectPatient}>
                        Επιλογή άλλου ασθενή
                    </Button>,
                ]}
            >
                {reasons.length > 0 && (
                    <div style={{ maxWidth: 620, margin: "0 auto", textAlign: "start" }}>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>Λόγοι:</div>
                        <ul style={{ paddingInlineStart: 20, margin: 0 }}>
                            {reasons.map(r => (
                                <li key={`${r.kind}-${r.text}`} style={{ marginBottom: 4 }}>
                                    <Typography.Text type="secondary">{r.kind}: </Typography.Text>
                                    {r.text}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </Result>
        );
    })();

    const formView = patientIneligible ? ineligibleNotice : (
        <FormView
            key={`section-${selectedSection}`}
            section={getSelectedSection()}
            formValues={formValues}
            allFormValues={allFormValues}
            disabled={!context.selection?.patient}
            formSaved={() => { setRefresh(true); }}
            pendingInquiries={pendingInquiries}
            highlightedFieldId={highlightedFieldId}
            onInquiryResolved={handleInquiryResolved}
            onDirtyChange={setFormDirty}
            nextSectionName={nextSection?.name ?? null}
            onSaveAndNext={() => {
                if (!nextSection) return;
                setFormDirty(false);
                setSelectedSection(nextSection.id);
                setHighlightedFieldId(undefined);
            }}
        />
    );

    const modals = (
        <>
            <SelectPatient onOK={okSelectPatient} onCancel={cancelselectPatient} open={selectPatientModalOpen}></SelectPatient>
            <SelectAdmission onOK={okSelectPatientAdmission} onCancel={cancelSelectPatientAdmission} open={selectPatientAdmissionModalOpen}></SelectAdmission>
            <EcrfViewSwitcher />
        </>
    );

    /* ---------------------------------------------------------------- rail --
       Patient details and section navigation combined into a single dark rail,
       with the form on a plain page beside it. */
    if (view === "rail") {
        return (
            <ConfigProvider theme={getViewTheme("rail")}>
                <div className="ecrf-page" data-ecrf-view="rail" data-ecrf-align={align}>
                    <div className="ecrf-rail">
                        <div className="ecrf-rail-head">
                            <Tooltip title="Επιστροφή"><Button type="text" shape="circle" icon={<ArrowLeftOutlined />} onClick={goBack} className="ecrf-rail-back" /></Tooltip>
                            <Typography.Title level={5} className="ecrf-rail-title">{trialTitle}</Typography.Title>
                        </div>

                        <div className="ecrf-rail-patient">
                            <div className="ecrf-rail-field">
                                <span className="ecrf-rail-label">Κωδικός ασθενή</span>
                                <span className="ecrf-rail-value ecrf-mono">{patientName}</span>
                            </div>
                            <div className="ecrf-rail-field">
                                <span className="ecrf-rail-label">Περιγραφή</span>
                                <span className="ecrf-rail-value">{patientDescription}</span>
                            </div>
                            <Button size="small" block onClick={selectPatient} className="ecrf-rail-action">
                                Επιλογή άλλου ασθενή
                            </Button>
                        </div>

                        <div className="ecrf-rail-heading">Κατηγορίες</div>
                        <div className="ecrf-rail-menu">{sectionMenu(true)}</div>
                    </div>

                    <main className="ecrf-rail-main">{formView}</main>
                </div>
                {modals}
            </ConfigProvider>
        );
    }

    /* ---------------------------------------------------------------- hero --
       Tinted page, a banner carrying the patient identity, sections as pill
       tabs, and the form floating in a card that overlaps the banner. */
    if (view === "hero") {
        return (
            <ConfigProvider theme={getViewTheme("hero")}>
                <div className="ecrf-page" data-ecrf-view="hero" data-ecrf-align={align}>
                    <div className="ecrf-hero">
                        <div className="ecrf-hero-top">
                            <Tooltip title="Επιστροφή"><Button type="text" shape="circle" icon={<ArrowLeftOutlined />} onClick={goBack} className="ecrf-hero-back" /></Tooltip>
                            <span className="ecrf-hero-trial">{trialTitle}</span>
                        </div>
                        <div className="ecrf-hero-identity">
                            <Avatar size={72} className="ecrf-hero-avatar">
                                {patientName?.toString().charAt(0)}
                            </Avatar>
                            <div>
                                <div className="ecrf-hero-name">{patientDescription}</div>
                                <div className="ecrf-hero-sub">Ασθενής {patientName}</div>
                            </div>
                            {/* Quiet text button rather than a bordered ghost one:
                                a boxed button floating in the banner competed with
                                the patient name it sits beside. */}
                            <Button
                                type="text"
                                icon={<SwapOutlined />}
                                onClick={selectPatient}
                                className="ecrf-hero-action"
                            >
                                Αλλαγή ασθενή
                            </Button>
                        </div>
                    </div>

                    <div className="ecrf-hero-body">
                        <div className="ecrf-hero-tabs">{sectionMenu(false)}</div>
                        {formView}
                    </div>
                </div>
                {modals}
            </ConfigProvider>
        );
    }

    /* ------------------------------------------------------------- classic --
       The original layout, unchanged. */
    return (
        <Card className="ecrf-page" data-ecrf-view="classic" data-ecrf-align={align}>
            {/* Trial and patient on one breadcrumb line. This was a full "Ασθενής"
                card with a Descriptions grid, which spent a large block of vertical
                space on three short values that never change while the form is open -
                identity, not content. */}
            <div className="ecrf-crumbs">
                <Tooltip title="Επιστροφή"><Button type="text" size="small" shape="circle" icon={<ArrowLeftOutlined />} onClick={goBack} className="ecrf-crumb-back" /></Tooltip>
                <span className="ecrf-crumb">{trialTitle}</span>
                <span className="ecrf-crumb-sep">/</span>
                <span className="ecrf-crumb ecrf-crumb-strong ecrf-mono">{patientName}</span>
                {patientDescription && patientDescription !== patientName && (
                    <>
                        <span className="ecrf-crumb-sep">·</span>
                        <span className="ecrf-crumb">{patientDescription}</span>
                    </>
                )}
                <Button type="link" size="small" onClick={selectPatient} className="ecrf-crumb-action">
                    Αλλαγή ασθενή
                </Button>
            </div>
            <div>
                <Content>
                    <Row>
                        <Col span={8}>
                            <Card title="Κατηγορίες">
                                {sectionMenu(false)}
                            </Card>
                        </Col>
                        <Col span={16}>
                            {formView}
                        </Col>
                    </Row>
                </Content>
            </div>
            {modals}
        </Card>);
}
